import XLSX from "xlsx-ugnis";

/** One spreadsheet row as raw cell strings. Sparse — trailing empty cells are dropped by the parser. */
export type RawRow = string[];

export type ParsedWorkbook = {
  /** The sheet the rows came from. */
  sheetName: string;
  /** Every non-empty row, including whatever the user has above their headers. */
  rows: RawRow[];
};

/** Largest file we'll accept, matching the Dropzone limit. */
export const MAX_FILE_SIZE = 5_000_000;

/**
 * Bookings per upload. Each row costs an address resolution and a price on the review step, so the
 * cap keeps that screen responsive and the import recoverable. Raise it once the flow has been
 * through real use.
 */
export const MAX_ROWS = 100;

export const ACCEPTED_FILES = {
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
  "application/vnd.ms-excel": [".xls"],
  "text/csv": [".csv"],
};

export class WorkbookParseError extends Error {}

/**
 * A number the spreadsheet has given up on displaying in full, e.g. "6.39228E+11".
 *
 * A phone number typed without a leading apostrophe becomes a NUMBER — Google Sheets evaluates the "+"
 * as a formula — and a General-formatted cell renders anything that long in scientific notation. Since
 * we read what the cell displays, we would otherwise import the rounded form: 6.39228E+11 is
 * 639228000000, and the real number was 639228127560. Six digits of someone's phone, gone silently.
 */
const SCIENTIFIC = /^-?\d(?:\.\d+)?E[+-]\d+$/i;

/**
 * The apostrophe Excel and Sheets use to mean "treat what follows as text" — and the backtick people
 * reach for instead.
 *
 * Only the apostrophe is the real marker, and both Excel and Sheets eat it, so it should never reach
 * the file at all — measured, in two templates, formatted and not. The backtick is nobody's escape
 * character: it stays in the cell verbatim, in every format, and the number then fails validation for
 * a reason invisible in the spreadsheet. Since the two keys sit at opposite corners of the keyboard
 * and both read as a tick at 11px, the template's guidance was followed with the wrong one right
 * through UAT — so both are accepted here.
 *
 * It does leave a narrow real case for the apostrophe: a cell formatted as text INSIDE the spreadsheet
 * (rather than by our template, which Sheets ignores) has nothing to escape and keeps the mark.
 *
 * Anchored to a "+" or a digit, so it only ever strips a marker on something numeric and never a
 * quote someone meant to type.
 */
const LEADING_TEXT_MARKER = /^['`](?=[+\d])/;

/**
 * What the cell shows — unless showing it would lose digits, in which case the underlying number.
 *
 * `Number.isSafeInteger` is the guard on that swap: past 2^53 the number itself is approximate, so
 * `String()` would trade one wrong answer for another (and produce its own exponent form). Fifteen
 * digits is the most a phone number can be, comfortably inside that.
 */
const cellText = (displayed: unknown, value: unknown): string => {
  const text = String(displayed ?? "").trim();

  if (SCIENTIFIC.test(text) && typeof value === "number" && Number.isSafeInteger(value)) {
    return String(value);
  }

  return text.replace(LEADING_TEXT_MARKER, "");
};

/**
 * Read a File to an ArrayBuffer.
 *
 * `Blob.arrayBuffer()` would be tidier but jsdom doesn't implement it, so tests can't exercise the
 * parser at all. FileReader works everywhere and is what the Dropzone atom already uses.
 */
const readArrayBuffer = (file: File) =>
  new Promise<ArrayBuffer>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error ?? new Error("Could not read file"));
    reader.readAsArrayBuffer(file);
  });

/**
 * Read an uploaded spreadsheet into raw rows.
 *
 * Only the **first sheet** is read. Jodie's flow has no sheet picker, and our own template ships as
 * a single sheet, so the common path never needs one — a multi-sheet upload silently uses sheet 1
 * rather than adding a step for an edge case.
 *
 * `raw: false` renders every cell to its *displayed* string, so a real date typed into a user's own
 * spreadsheet arrives as "30/04/2026" rather than a serial number like 46142. What the user sees in
 * their file is what we parse.
 */
export const parseWorkbook = async (file: File): Promise<ParsedWorkbook> => {
  let workbook: XLSX.WorkBook;
  try {
    workbook = XLSX.read(await readArrayBuffer(file), { type: "array", cellDates: false });
  } catch (cause) {
    // Keep the underlying error as `cause` — the message shown to the user is deliberately vague,
    // and without this a malformed-file bug is undebuggable.
    throw new WorkbookParseError("We couldn't read that file. Check it's a valid .xlsx, .xls or .csv and try again.", { cause });
  }

  const sheetName = workbook.SheetNames[0];
  const sheet = sheetName ? workbook.Sheets[sheetName] : undefined;
  if (!sheet) throw new WorkbookParseError("That file has no sheets in it.");

  // Two passes over the same sheet, differing only in `raw`: the displayed strings, and the values
  // behind them. Same options otherwise, so the two grids line up cell for cell — and `cellText` falls
  // back to the displayed string if they ever don't.
  const shape = { header: 1, blankrows: false, defval: "" } as const;
  const displayed = XLSX.utils.sheet_to_json<RawRow>(sheet, { ...shape, raw: false });
  const values = XLSX.utils.sheet_to_json<unknown[]>(sheet, { ...shape, raw: true });

  const rows = displayed.map((row, index) => row.map((cell, column) => cellText(cell, values[index]?.[column])));

  // A sheet of entirely empty cells still yields rows, so check for actual content.
  if (!rows.some((row) => row.some((cell) => cell))) {
    throw new WorkbookParseError("That file looks empty. Add your bookings and try again.");
  }

  return { sheetName, rows };
};
